import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { matchRoutes, renderRoutes } from 'react-router-config';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Col, Container, Row } from 'reactstrap';
import { isEqual } from 'lodash';

import {
    AppAside,
    AppBreadcrumb,
    AppFooter,
    AppHeader,
    AppSidebar,
    AppSidebarFooter,
    AppSidebarForm,
    AppSidebarHeader,
    AppSidebarMinimizer,
    AppSidebarNav,
} from '@coreui/react';

import { useNotification } from 'context/NotificationContext';

import APIUtils from 'apis/APIUtils';
// routes config
// api
import AuthAPI from 'apis/AuthAPI';
import DashboardAPI from 'apis/DashboardAPI';

import { formatNumberWithComma } from 'components/utils/format';
import { accountType } from 'components/utils/function';

import DefaultHeader from './DefaultHeader';
const DefaultAside = React.lazy(() => import('./DefaultAside'));
const DefaultFooter = React.lazy(() => import('./DefaultFooter'));

const DefaultLayout = (props) => {
    const [t] = useTranslation();
    const loading = () => <div className="animated fadeIn pt-1 text-center"><div className="sk-spinner sk-spinner-pulse" /></div>;
    const { route, currentUser, ...attributes } = props;
    const [navigation, setNavigation] = useState({ items: [] });
    const [pageTitle, setPageTitle] = useState();
    const history = useHistory();
    const [notification, dispatchNotification] = useNotification();
    const [needBillRequest, setNeedBillRequest] = useState(false);

    const handleHeaderTitle = useCallback(() => {
        const branch = matchRoutes(route.routes, window.location.pathname);
        if (branch && branch[0]) {
            setPageTitle(branch[0].route.name);
        }
    }, [route.routes]);

    useEffect(() => {
        if (needBillRequest) {
            if (accountType() !== 'Admin' && !accountType('master') && !notification.isBillUpdated) {
                DashboardAPI.getNaviBillInfo().then(response => {
                    dispatchNotification({
                        type: 'update_totalbill_balance',
                        outstandingBalance: response?.outstanding_balance || 0,
                        availableCreditLimit: response?.available_credit_limit || 0,
                        totalOutstanding: response?.total_outstanding || 0,
                        totalCreditLimit: response?.total_credit_limit || 0,
                        onlineMember: response?.online_member || 0,
                        fakeOnline: response?.fake_online || 0,
                        websiteStatus: response?.website_status || '',
                        lastUpdate: response?.last_update || '',
                    });
                }).catch(err => {
                    toast.error(t(err.message), { toastId: err.message });
                });
            }
        }

        if (!needBillRequest && !notification.isBillUpdated) {
            setTimeout(()=> {
                setNeedBillRequest(true);
            }, 1000);
        }
    }, [needBillRequest]);

    useEffect(() => {
        async function getNav() {
            await AuthAPI.getSideBar().then((rs) => {
                let nav = [rs.NavHeader, ...rs.NavItems];
                nav.forEach(item => {
                    item.name = t(item.name);
                    if (item.children) {
                        const newChild = [];
                        item.children.forEach(child => {
                            child.name = t(child.name);
                            const findChild = route?.routes?.find(routeItem => isEqual(child?.url, routeItem?.path));
                            if (!findChild?.hideOnMenu) {
                                newChild.push(child);
                            }
                        });
                        item.children = newChild;
                    }
                });
                setNavigation({ items: nav });
            }).catch((err) => {
                console.error(err);
            });
        }
        getNav();

        handleHeaderTitle();

        // handle webbrowser change url
        history.listen(()=> {
            handleHeaderTitle();
        });
    }, [t]);

    const signOut = (e) => {
        e.preventDefault();
        APIUtils.logOut();
        // window.location.reload()
    };

    const goToProfile = (e) => {
        e.preventDefault();
        history.push('/profile');
    };

    const renderHeader = ()=> {
        if (accountType() !== 'Admin' && !accountType('master')) {
            return (
                <AppSidebarHeader className='py-1 px-2'>
                    <Col className='font-weight-bold'>
                        <Row>
                            <div className='text-dark'>{t('Outstanding Balance')}:&nbsp;</div>
                            <div className='text-danger'> {formatNumberWithComma(notification.outstandingBalance)}</div>
                        </Row>
                        <Row>
                            <div className='text-dark'>{t('Total Credit Limit')}:&nbsp;</div>
                            <div className='text-danger'> {formatNumberWithComma(notification.totalCreditLimit)}</div>
                        </Row>
                    </Col>
                </AppSidebarHeader>
            );
        }

        return <AppSidebarHeader />;
    };

    return (
        <div className="app">
            <Helmet>
                {pageTitle && <title>{pageTitle}</title>}
            </Helmet>
            <AppHeader fixed>
                <Suspense fallback={loading()}>
                    <DefaultHeader goToProfile={e => goToProfile(e)} onLogout={e => signOut(e)} currentUser={currentUser} />
                </Suspense>
            </AppHeader>
            <div className="app-body">
                <AppSidebar fixed display="lg">
                    {renderHeader()}
                    <AppSidebarForm />
                    <Suspense>
                        <AppSidebarNav navConfig={navigation} {...attributes} />
                    </Suspense>
                    <AppSidebarFooter />
                    <AppSidebarMinimizer />
                </AppSidebar>
                <main className="main">
                    <AppBreadcrumb appRoutes={route.routes} />
                    <Container fluid>
                        <p />
                        <Suspense fallback={loading()}>
                            {renderRoutes(route.routes, { currentUser: currentUser })}
                        </Suspense>
                    </Container>
                </main>
                <AppAside fixed>
                    <Suspense fallback={loading()}>
                        <DefaultAside />
                    </Suspense>
                </AppAside>
            </div>
            <AppFooter>
                <Suspense fallback={loading()}>
                    <DefaultFooter />
                </Suspense>
            </AppFooter>
        </div>
    );
};

export default DefaultLayout;
